import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

export const magnitudeAndLevels: WikiTopic = {
  slug: "magnitude-and-levels",
  title: "Magnitude, stops, and targets",
  summary:
    "Every strategy measures its own magnitude in its own unit, and three types carry no levels at all.",
  group: "concepts",
  sections: [
    {
      heading: "Every scale is the strategy's own",
      code: "magnitude",
      blocks: [
        p(
          <>
            Each row carries one <Datum>magnitude</Datum>, and each strategy decides for itself what
            that number measures. Nine of them do, and the units do not line up.
          </>,
        ),
        table(
          "What magnitude measures, by strategy",
          ["Signal type", "What it measures", "Unit"],
          [
            [
              "ema_cross_*",
              <>
                Gap between EMA <Datum key="f">{QUOTED.EMA_CROSS_FAST}</Datum> and EMA{" "}
                <Datum key="s">{QUOTED.EMA_CROSS_SLOW}</Datum> at the cross
              </>,
              "Hourly ATR multiples",
            ],
            [
              "ema_vwap_cross_*",
              <>
                Gap between EMA <Datum key="e">{QUOTED.HOURLY_CROSS_EMA_PERIOD}</Datum> and VWAP at
                the cross
              </>,
              "Hourly ATR multiples",
            ],
            [
              "volume_breakout_*",
              <>
                Bucket volume over the median of the last{" "}
                <Datum key="w">{QUOTED.VOLUME_BREAKOUT_WINDOW}</Datum> buckets
              </>,
              "A raw multiple",
            ],
            [
              "pivot_bounce / pivot_rejection",
              <>
                Distance past the RSI threshold that was crossed:{" "}
                <Datum key="o">{QUOTED.MOMENTUM_RSI_OVERSOLD}</Datum> minus the reading, or the
                reading minus <Datum key="b">{QUOTED.MOMENTUM_RSI_OVERBOUGHT}</Datum>
              </>,
              "RSI points",
            ],
            [
              "gap_up / gap_down",
              <>
                Overnight gap over the <Datum key="g">{QUOTED.GAP_ATR_PERIOD}</Datum>-period daily
                ATR
              </>,
              "Daily ATR multiples",
            ],
            [
              "pivot_trend_*",
              "Run length in sessions plus the fractional ATR distance past the CPR edge",
              "Sessions plus ATR",
            ],
            [
              "prophet_swing_entry",
              <>
                Reward from entry to target over the{" "}
                <Datum key="d">{QUOTED.DAILY_ATR_PERIOD}</Datum>-period daily ATR
              </>,
              "Daily ATR multiples",
            ],
            ["prophet_swing_exit", "The absolute R multiple the closed trade returned", "R"],
            ["ict_long / ict_short", "Reward percent over risk percent", "A ratio"],
          ],
        ),
        p(
          <>
            Two of those choices look odd until you read why. The exit's magnitude is absolute
            because severity reads magnitude as loudness, and a <Datum>-1R</Datum> exit is as worth
            knowing as a <Datum>+3R</Datum> one; the sign stays in the payload. And the prophet
            entry and the pivot trend both refuse to use reward over risk, which climbs as the stop
            tightens and would make the worst entries the loudest. Reward over risk survives as{" "}
            <Datum>rewardRisk</Datum> in both payloads, where it is readable and steers nothing.
          </>,
        ),
      ],
    },
    {
      heading: "Magnitude does not sort across strategies",
      blocks: [
        p(
          <>
            A magnitude of <Datum>3</Datum> on a volume breakout is three times median volume. The
            same <Datum>3</Datum> on an EMA cross is three ATR of separation, and on a pivot bounce
            it is an RSI reading of <Datum>{QUOTED.MOMENTUM_RSI_OVERSOLD - 3}</Datum>. Sorting a
            mixed feed by magnitude compares numbers that share no unit.
          </>,
        ),
        p(
          <>
            Within one strategy the ordering is real, and that is the ranking to use. Pick a signal
            type first, then sort.
          </>,
        ),
        note(
          <>
            The severity tables key their thresholds by signal type for the same reason, so two
            types never share a scale. Both tables ship empty: the only way to turn a
            strategy-relative magnitude into a threshold is to measure its distribution first, and
            inventing one produces a plausible alert, which is worse than no alert.
          </>,
        ),
      ],
    },
    {
      heading: "Where the stops and targets come from",
      code: "stop_loss / take_profit",
      blocks: [
        p(
          <>
            Most hourly types stop at the bar that fired them and target twice that distance. The
            two that do not are the two whose trade is a move back to a level somebody else set.
          </>,
        ),
        table(
          "Stop and target arithmetic, by strategy",
          ["Strategy", "Stop", "Target"],
          [
            [
              "EMA 9/21 cross, EMA/VWAP cross",
              "The firing bar's low on a long, its high on a short",
              "Twice the distance from the close to that stop",
            ],
            [
              "Momentum pivot",
              "S1, pushed out by a fifth of the pivot-to-S1 distance, mirrored at R1",
              "The CPR pivot",
            ],
            [
              "Gap detector",
              "The open, half a daily ATR into the gap",
              "The previous session's close",
            ],
            [
              "Pivot trend",
              "The nearest CPR ladder level the other way, buffered in daily ATR",
              "The nearest ladder level in the trade's direction",
            ],
            [
              "Prophet swing entry",
              <>
                The <Datum key="l">{QUOTED.PIVOT_LOOKBACK_SESSIONS}</Datum>-session pivot low, less{" "}
                <Datum key="p">{QUOTED.PIVOT_BUFFER_ATR}</Datum> daily ATR
              </>,
              "The upper Bollinger band on the daily",
            ],
            [
              "ICT composite",
              <>
                Below every level the setup rests on, buffered{" "}
                <Datum key="s">{QUOTED.SWING_STOP_BUFFER_ATR}</Datum> ATR
              </>,
              "The previous completed week's extreme, or the nearest prior daily swing past entry",
            ],
          ],
        ),
        p(
          <>
            The gap detector targets the previous close because the trade is the fill, not the gap.
            A gap up is a short back toward where the stock closed yesterday, which is why a bucket
            that has already traded back through that close emits nothing: the move is over, and the
            row would carry an entry on the wrong side of its own target.
          </>,
        ),
        note(
          <>
            The crossover arithmetic is the reference implementation's, unchanged, and nothing in
            this app acts on any of these levels. They are what a reader measures the trade against.
          </>,
        ),
      ],
    },
    {
      heading: "The three types with no levels",
      code: "stop_loss IS NULL",
      blocks: [
        p(
          <>
            Three signal types write both levels as <Datum>null</Datum>, and the feed labels those
            rows "no levels" rather than drawing a zero.
          </>,
        ),
        table(
          "Types that carry neither level",
          ["Signal type", "Why"],
          [
            [
              "volume_breakout_bullish",
              "Nothing in the setup measures a level, and a level nothing measured is worse than no level",
            ],
            [
              "volume_breakout_bearish",
              "Nothing in the setup measures a level, and a level nothing measured is worse than no level",
            ],
            [
              "prophet_swing_exit",
              "The exit closes the position; the stop the trade ran under is payload.entryStopLoss",
            ],
          ],
        ),
        p(
          <>
            Confluence rows are not on that list. Scoring copies the nearest stop and the nearest
            target off the agreeing votes, so a <Datum>confluence_*</Datum> row usually carries
            both. It carries neither when every vote behind it was levelless, which is why the chart
            overlays read the payload instead of trusting the type.
          </>,
        ),
      ],
    },
  ],
};
